import {
  ActionIcon,
  Badge,
  Button,
  Group,
  MultiSelect,
  NumberInput,
  Select,
  Stack,
  Text,
  TextInput,
} from '@mantine/core';
import { IconCirclePlus, IconTrash } from '@tabler/icons-react';
import { useMemo, useState } from 'react';

/**
 * UI for the saved-view filter DSL (Phase B 3.3). Supports a
 * top-level AND of field predicates — covers the SMB use case
 * (90% of "smart segments" are intersection of conditions). For
 * arbitrary nesting we'd open a tree editor; deferred until vendors
 * actually ask for it.
 *
 * Beyond scalar columns it can also build Group / Label membership and
 * Custom-field conditions (field key `cf:<id>`), matching the backend
 * compiler in `view-dsl.ts`. Those three need their option lists, so the
 * caller passes `groups` / `labels` / `customFields`.
 */

export type FieldPredicate = {
  kind: 'field';
  field: string;
  op: string;
  value: string | number | boolean | string[] | number[] | null;
};
export type AndTree = { kind: 'and'; terms: FieldPredicate[] };

export type ContactOption = { id: number; title: string };
export type CustomFieldOption = { id: number; name: string };

type FieldType = 'text' | 'number' | 'date' | 'enum' | 'group' | 'label';

const FIELDS: Array<{ value: string; label: string; type: FieldType }> = [
  { value: 'first_name', label: 'First name', type: 'text' },
  { value: 'last_name', label: 'Last name', type: 'text' },
  { value: 'email', label: 'Email', type: 'text' },
  { value: 'wa_id', label: 'WhatsApp number', type: 'text' },
  { value: 'language_code', label: 'Language', type: 'text' },
  { value: 'engagement_score', label: 'Engagement score (0-100)', type: 'number' },
  { value: 'unread_count', label: 'Unread count', type: 'number' },
  { value: 'last_messaged', label: 'Last messaged', type: 'date' },
  { value: 'created_at', label: 'Created at', type: 'date' },
  { value: 'whatsapp_opt_out', label: 'WhatsApp opt-out', type: 'enum' },
];

const TEXT_OPS = [
  { value: 'eq', label: 'is' },
  { value: 'neq', label: 'is not' },
  { value: 'contains', label: 'contains' },
  { value: 'starts_with', label: 'starts with' },
  { value: 'ends_with', label: 'ends with' },
  { value: 'is_null', label: 'is empty' },
  { value: 'not_null', label: 'is not empty' },
];
const NUMBER_OPS = [
  { value: 'eq', label: '=' },
  { value: 'neq', label: '≠' },
  { value: 'gt', label: '>' },
  { value: 'gte', label: '≥' },
  { value: 'lt', label: '<' },
  { value: 'lte', label: '≤' },
];
const DATE_OPS = [
  { value: 'gte', label: 'after' },
  { value: 'lte', label: 'before' },
  { value: 'is_null', label: 'is empty' },
  { value: 'not_null', label: 'is not empty' },
];
const ENUM_OPS = [
  { value: 'is_null', label: 'opted in' },
  { value: 'not_null', label: 'opted out' },
];
const GROUP_OPS = [{ value: 'in_group', label: 'is any of' }];
const LABEL_OPS = [{ value: 'has_label', label: 'is any of' }];

function fieldType(field: string): FieldType {
  if (field === 'group') return 'group';
  if (field === 'label') return 'label';
  // cf:* and anything unknown is treated as text (matches the DSL's cf handling).
  return FIELDS.find((x) => x.value === field)?.type ?? 'text';
}

const opsForField = (field: string) => {
  switch (fieldType(field)) {
    case 'number':
      return NUMBER_OPS;
    case 'date':
      return DATE_OPS;
    case 'enum':
      return ENUM_OPS;
    case 'group':
      return GROUP_OPS;
    case 'label':
      return LABEL_OPS;
    default:
      return TEXT_OPS;
  }
};

const defaultValueFor = (field: string): FieldPredicate['value'] => {
  const t = fieldType(field);
  return t === 'group' || t === 'label' ? [] : '';
};

export function FilterBuilder({
  value,
  onChange,
  groups = [],
  labels = [],
  customFields = [],
}: {
  value: AndTree | null;
  onChange: (next: AndTree | null) => void;
  groups?: ContactOption[];
  labels?: ContactOption[];
  customFields?: CustomFieldOption[];
}) {
  const [terms, setTerms] = useState<FieldPredicate[]>(value?.terms ?? []);

  // Field picker: scalar columns + (when available) Group, Label, and one entry
  // per custom field, keyed `cf:<id>` so the backend DSL compiles it.
  const fieldOptions = useMemo(() => {
    const opts = FIELDS.map((f) => ({ value: f.value, label: f.label }));
    if (groups.length) opts.push({ value: 'group', label: 'Group' });
    if (labels.length) opts.push({ value: 'label', label: 'Label' });
    for (const cf of customFields) opts.push({ value: `cf:${cf.id}`, label: cf.name });
    return opts;
  }, [groups, labels, customFields]);

  const propagate = (next: FieldPredicate[]) => {
    setTerms(next);
    onChange(next.length === 0 ? null : { kind: 'and', terms: next });
  };

  const addTerm = () =>
    propagate([...terms, { kind: 'field', field: 'first_name', op: 'contains', value: '' }]);

  const updateTerm = (i: number, patch: Partial<FieldPredicate>) =>
    propagate(terms.map((t, idx) => (idx === i ? { ...t, ...patch } : t)));

  const removeTerm = (i: number) => propagate(terms.filter((_, idx) => idx !== i));

  return (
    <Stack gap="xs">
      {terms.length === 0 && (
        <Text c="dimmed" size="sm">
          No filters yet — every contact matches. Add a condition to narrow it.
        </Text>
      )}
      {terms.map((t, i) => {
        const ops = opsForField(t.field);
        const ft = fieldType(t.field);
        const noValue = t.op === 'is_null' || t.op === 'not_null';
        const selectedIds = Array.isArray(t.value) ? t.value.map(String) : [];
        return (
          <Group key={i} wrap="nowrap" gap="xs" align="end">
            <Select
              w={170}
              size="sm"
              searchable
              data={fieldOptions}
              value={t.field}
              onChange={(v) => {
                const nf = v ?? 'first_name';
                updateTerm(i, {
                  field: nf,
                  op: opsForField(nf)[0]!.value,
                  value: defaultValueFor(nf),
                });
              }}
            />
            <Select
              w={130}
              size="sm"
              data={ops}
              value={t.op}
              onChange={(v) => updateTerm(i, { op: v ?? 'eq' })}
            />
            {!noValue &&
              (ft === 'group' || ft === 'label' ? (
                <MultiSelect
                  w={220}
                  size="sm"
                  searchable
                  placeholder="Select…"
                  data={(ft === 'group' ? groups : labels).map((o) => ({
                    value: String(o.id),
                    label: o.title,
                  }))}
                  value={selectedIds}
                  onChange={(vals) => updateTerm(i, { value: vals })}
                />
              ) : (
                <ValueInput
                  fieldType={ft}
                  value={t.value as string | number}
                  onChange={(v) => updateTerm(i, { value: v })}
                />
              ))}
            <ActionIcon variant="subtle" color="red" onClick={() => removeTerm(i)}>
              <IconTrash size={14} />
            </ActionIcon>
          </Group>
        );
      })}
      <Group gap="xs">
        <Button
          variant="default"
          size="xs"
          leftSection={<IconCirclePlus size={14} />}
          onClick={addTerm}
        >
          Add condition
        </Button>
        {terms.length >= 2 && (
          <Badge variant="light" size="sm">
            ALL of {terms.length} conditions
          </Badge>
        )}
      </Group>
    </Stack>
  );
}

function ValueInput(props: {
  fieldType: FieldType;
  value: string | number;
  onChange: (v: string | number) => void;
}) {
  if (props.fieldType === 'number') {
    return (
      <NumberInput
        size="sm"
        w={120}
        value={typeof props.value === 'number' ? props.value : Number(props.value) || 0}
        onChange={(v) => props.onChange(typeof v === 'number' ? v : Number(v) || 0)}
      />
    );
  }
  if (props.fieldType === 'date') {
    return (
      <TextInput
        size="sm"
        w={170}
        value={String(props.value ?? '')}
        onChange={(e) => props.onChange(e.currentTarget.value)}
        placeholder="30d_ago / 2026-01-01"
      />
    );
  }
  return (
    <TextInput
      size="sm"
      w={200}
      value={String(props.value ?? '')}
      onChange={(e) => props.onChange(e.currentTarget.value)}
    />
  );
}
